/**
 * Mini-site `pages` keys for PUT /club/website/:id and full club update.
 * Each block: { title?, body? }
 * Branding includes `instagramurl` (JSON); send `""` to clear when updating a previously set value (see `buildClubWebsitePutBody` baseline).
 */
import { buildClubBillingPutFields } from "../helpers/clubBilling";

export const CLUB_WEBSITE_PAGE_KEYS = [
  { key: "eventsPageIntro", labelKey: "CLUB_PAGE_EVENTS_INTRO" },
  { key: "ourServices", labelKey: "CLUB_PAGE_OUR_SERVICES" },
  { key: "cancellationRefund", labelKey: "CLUB_PAGE_CANCELLATION_REFUND" },
  { key: "termsConditions", labelKey: "CLUB_PAGE_TERMS" },
  { key: "privacyPolicy", labelKey: "CLUB_PAGE_PRIVACY" },
  { key: "contactUs", labelKey: "CLUB_PAGE_CONTACT_US" },
];

/** Branding fields allowed on PUT /club/website/:id (all optional). */
export const CLUB_WEBSITE_BRANDING_KEYS = [
  "logo",
  "primarycolor",
  "secondarycolor",
  "theme",
  /** Social / links — empty string on PUT clears stored value (API also accepts instagramUrl). */
  "instagramurl",
  "accentcolor",
  "textcolor",
  "surfacecolor",
  "bodyfontfamily",
  "buttontextcolor",
  "buttonborderradius",
  "buttonhovercolor",
  "pagebackgroundimageurl",
  "pagebackgroundimageopacity",
  "herobackgroundimageurl",
  "herobackgroundimageopacity",
  "footerbackgroundimageurl",
  "footerbackgroundimageopacity",
  "customcss",
];

/** Default mini-site text color when unset (advanced branding). */
export const DEFAULT_CLUB_TEXT_COLOR = "#000000";

export const EMPTY_CLUB_WEBSITE_BRANDING = Object.fromEntries(
  CLUB_WEBSITE_BRANDING_KEYS.map((k) => [
    k,
    k === "textcolor" ? DEFAULT_CLUB_TEXT_COLOR : "",
  ])
);

/** Branding fields edited in the main club form (logo also via file + textarea). */
export const CLUB_FORM_CORE_BRANDING_KEYS = [
  "logo",
  "primarycolor",
  "secondarycolor",
  "theme",
];

/** Extra mini-site branding (admin full edit + website editor advanced). */
export const CLUB_EXTENDED_BRANDING_KEYS = CLUB_WEBSITE_BRANDING_KEYS.filter(
  (k) => !CLUB_FORM_CORE_BRANDING_KEYS.includes(k)
);

export const EMPTY_EXTENDED_CLUB_BRANDING = Object.fromEntries(
  CLUB_EXTENDED_BRANDING_KEYS.map((k) => [
    k,
    k === "textcolor" ? DEFAULT_CLUB_TEXT_COLOR : "",
  ])
);

/** Background URLs: empty string must be sent on save so the API can clear stored images. */
export const CLUB_BRAND_BACKGROUND_IMAGE_KEYS = [
  "pagebackgroundimageurl",
  "herobackgroundimageurl",
  "footerbackgroundimageurl",
];

/** Optional URL fields where `""` must appear in JSON so the backend clears the DB column. */
export const CLUB_BRAND_OPTIONAL_URL_CLEAR_WITH_EMPTY = ["instagramurl"];

/** Public club page carousel — max images per club. */
export const BRANDING_GALLERY_MAX = 10;

const BRANDING_GALLERY_READ_ALIASES = [
  "brandinggallery",
  "brandingGallery",
  "branding_gallery",
  "gallery",
  "galleryImages",
];

/**
 * @param {unknown} item
 * @returns {string | null}
 */
function normalizeGalleryReadEntry(item) {
  if (typeof item === "string") {
    const s = item.trim();
    return s || null;
  }
  if (item && typeof item === "object" && item.url != null) {
    const s = String(item.url).trim();
    return s || null;
  }
  return null;
}

/** Quick-pick swatches (same UX as primary: native picker + hex + presets). */
export const PRESET_CLUB_BRAND_COLORS = [
  "#000000",
  "#ffffff",
  "#e53e3e",
  "#dd6b20",
  "#d69e2e",
  "#38a169",
  "#3182ce",
  "#805ad5",
  "#718096",
];

/**
 * Advanced branding rows (excludes `customcss`, rendered separately).
 * Used by admin club modal and club website editor.
 */
export const CLUB_BRAND_ADVANCED_UI = [
  {
    key: "instagramurl",
    labelKey: "CLUB_BRAND_INSTAGRAM_URL",
    wide: true,
    placeholderKey: "CLUB_BRAND_INSTAGRAM_URL_PH",
  },
  { key: "accentcolor", labelKey: "CLUB_BRAND_ACCENT", color: true },
  { key: "textcolor", labelKey: "CLUB_BRAND_TEXT", color: true },
  { key: "surfacecolor", labelKey: "CLUB_BRAND_SURFACE", color: true },
  { key: "bodyfontfamily", labelKey: "CLUB_BRAND_FONT_BODY" },
  { key: "buttontextcolor", labelKey: "CLUB_BRAND_BUTTON_TEXT", color: true },
  { key: "buttonborderradius", labelKey: "CLUB_BRAND_BUTTON_RADIUS" },
  { key: "buttonhovercolor", labelKey: "CLUB_BRAND_BUTTON_HOVER", color: true },
  {
    key: "pagebackgroundimageurl",
    labelKey: "CLUB_BRAND_IMG_PAGE_BG",
    wide: true,
    imageUrl: true,
    opacityKey: "pagebackgroundimageopacity",
  },
  {
    key: "herobackgroundimageurl",
    labelKey: "CLUB_BRAND_IMG_HERO",
    wide: true,
    imageUrl: true,
    opacityKey: "herobackgroundimageopacity",
  },
  {
    key: "footerbackgroundimageurl",
    labelKey: "CLUB_BRAND_IMG_FOOTER",
    wide: true,
    imageUrl: true,
    opacityKey: "footerbackgroundimageopacity",
  },
];

export function normalizeClubPages(raw) {
  const out = {};
  CLUB_WEBSITE_PAGE_KEYS.forEach(({ key }) => {
    out[key] = { title: "", body: "" };
  });
  if (raw && typeof raw === "object") {
    CLUB_WEBSITE_PAGE_KEYS.forEach(({ key }) => {
      const p = raw[key];
      if (p && typeof p === "object") {
        out[key] = {
          title: p.title != null ? String(p.title) : "",
          body: p.body != null ? String(p.body) : "",
        };
      }
    });
  }
  return out;
}

export function pagesToPayload(pages) {
  const payload = {};
  CLUB_WEBSITE_PAGE_KEYS.forEach(({ key }) => {
    const p = pages[key] || { title: "", body: "" };
    payload[key] = {
      title: (p.title || "").trim(),
      body: (p.body || "").trim(),
    };
  });
  return payload;
}

const OPACITY_READ_ALIASES = [
  [
    "pagebackgroundimageopacity",
    ["pageBackgroundImageOpacity", "page_background_image_opacity"],
  ],
  [
    "herobackgroundimageopacity",
    ["heroBackgroundImageOpacity", "hero_background_image_opacity"],
  ],
  [
    "footerbackgroundimageopacity",
    ["footerBackgroundImageOpacity", "footer_background_image_opacity"],
  ],
];

function mergeOpacityAliasesFromApi(raw, out) {
  OPACITY_READ_ALIASES.forEach(([canonical, alts]) => {
    if (String(out[canonical] || "").trim() !== "") return;
    for (let i = 0; i < alts.length; i += 1) {
      const v = raw[alts[i]];
      if (v != null && v !== "") {
        out[canonical] = String(v);
        break;
      }
    }
  });
}

const INSTAGRAM_READ_ALIASES = [
  "instagramUrl",
  "instagram_url",
  "instagramURL",
];

function mergeInstagramAliasesFromApi(raw, out) {
  if (String(out.instagramurl || "").trim() !== "") return;
  for (let i = 0; i < INSTAGRAM_READ_ALIASES.length; i += 1) {
    const v = raw[INSTAGRAM_READ_ALIASES[i]];
    if (v != null && v !== "") {
      out.instagramurl = String(v);
      break;
    }
  }
}

/**
 * Optional key aliases for strict APIs (opacity fields).
 * `REACT_APP_CLUB_FONT_SIZES_CAMELCASE=true` → camelCase opacity keys.
 * `REACT_APP_CLUB_FONT_SIZES_SNAKE_CASE=true` → snake_case opacity keys.
 *
 * @param {Record<string, unknown>} body
 */
export function applyClubFontSizeKeyAliases(body) {
  if (!body || typeof body !== "object") return body;
  if (process.env.REACT_APP_CLUB_FONT_SIZES_CAMELCASE === "true") {
    const out = { ...body };
    const map = {
      pagebackgroundimageopacity: "pageBackgroundImageOpacity",
      herobackgroundimageopacity: "heroBackgroundImageOpacity",
      footerbackgroundimageopacity: "footerBackgroundImageOpacity",
    };
    Object.entries(map).forEach(([low, camel]) => {
      if (Object.prototype.hasOwnProperty.call(out, low)) {
        out[camel] = out[low];
        delete out[low];
      }
    });
    return out;
  }
  if (process.env.REACT_APP_CLUB_FONT_SIZES_SNAKE_CASE === "true") {
    const out = { ...body };
    const map = {
      pagebackgroundimageopacity: "page_background_image_opacity",
      herobackgroundimageopacity: "hero_background_image_opacity",
      footerbackgroundimageopacity: "footer_background_image_opacity",
    };
    Object.entries(map).forEach(([low, snake]) => {
      if (Object.prototype.hasOwnProperty.call(out, low)) {
        out[snake] = out[low];
        delete out[low];
      }
    });
    return out;
  }
  return body;
}

/**
 * Normalize gallery array from API (aliases, strings only, max length).
 * @param {Record<string, unknown> | null | undefined} raw
 * @returns {string[]}
 */
export function pickBrandingGalleryFromClub(raw) {
  if (!raw || typeof raw !== "object") return [];
  for (let i = 0; i < BRANDING_GALLERY_READ_ALIASES.length; i += 1) {
    const v = raw[BRANDING_GALLERY_READ_ALIASES[i]];
    if (Array.isArray(v)) {
      return v
        .map(normalizeGalleryReadEntry)
        .filter(Boolean)
        .slice(0, BRANDING_GALLERY_MAX);
    }
  }
  return [];
}

/** Allowed in PUT body: https URL or base64 data:image/... (never blob:). */
export function isBrandingGallerySendableItem(item) {
  const s = String(item ?? "").trim();
  if (!s) return false;
  if (/^blob:/i.test(s)) return false;
  if (/^data:image\//i.test(s)) return true;
  if (/^https?:\/\//i.test(s)) return true;
  return false;
}

/**
 * @param {string[]} gallery
 * @returns {string[]} blob: or other invalid entries (blocked before PUT)
 */
export function findInvalidBrandingGalleryItems(gallery) {
  if (!Array.isArray(gallery)) return [];
  return gallery.filter((item) => {
    const s = String(item ?? "").trim();
    return s && !isBrandingGallerySendableItem(s);
  });
}

/**
 * Build ordered gallery payload for PUT (existing URLs or base64 data URLs).
 * Never includes blob: URLs — backend cannot store those.
 * @param {string[]} gallery
 * @returns {string[]}
 */
export function buildBrandingGalleryPayload(gallery) {
  if (!Array.isArray(gallery)) return [];
  return gallery
    .filter((item) => isBrandingGallerySendableItem(item))
    .map((s) => String(s).trim())
    .slice(0, BRANDING_GALLERY_MAX);
}

export function pickWebsiteBrandingFromClub(raw) {
  if (!raw || typeof raw !== "object") return { ...EMPTY_CLUB_WEBSITE_BRANDING };
  const out = { ...EMPTY_CLUB_WEBSITE_BRANDING };
  CLUB_WEBSITE_BRANDING_KEYS.forEach((k) => {
    const v = raw[k];
    if (v != null && v !== "") out[k] = String(v);
  });
  mergeOpacityAliasesFromApi(raw, out);
  mergeInstagramAliasesFromApi(raw, out);
  return out;
}

export function pickExtendedBrandingFromClub(raw) {
  if (!raw || typeof raw !== "object") return { ...EMPTY_EXTENDED_CLUB_BRANDING };
  const out = { ...EMPTY_EXTENDED_CLUB_BRANDING };
  CLUB_EXTENDED_BRANDING_KEYS.forEach((k) => {
    const v = raw[k];
    if (v != null && v !== "") out[k] = String(v);
  });
  mergeOpacityAliasesFromApi(raw, out);
  mergeInstagramAliasesFromApi(raw, out);
  return out;
}

/**
 * Build PUT /club/website body: branding keys + pages. For `instagramurl`, empty string
 * is included only when `options.baselineBranding` shows the field was previously set,
 * so unrelated saves do not clear stored values.
 * @param {Record<string, string>} branding
 * @param {ReturnType<typeof normalizeClubPages>} pagesForm
 * @param {{ baselineBranding?: Record<string, string> | null }} [options]
 */
export function buildClubWebsitePutBody(branding, pagesForm, options = {}) {
  const baseline = options?.baselineBranding;
  const pages = pagesToPayload(pagesForm);
  const body = { pages };
  if (options?.billing && typeof options.billing === "object") {
    Object.assign(body, buildClubBillingPutFields(options.billing));
  }
  const clearWhenEmpty = new Set(CLUB_BRAND_OPTIONAL_URL_CLEAR_WITH_EMPTY);
  CLUB_WEBSITE_BRANDING_KEYS.forEach((k) => {
    const raw = branding[k];
    const s = raw != null ? String(raw).trim() : "";
    if (s === "") {
      if (clearWhenEmpty.has(k) && baseline && typeof baseline === "object") {
        const prev = String(baseline[k] ?? "").trim();
        if (prev !== "") body[k] = "";
      }
      return;
    }
    body[k] = s;
  });
  if (options.brandingGallery !== undefined) {
    body.brandinggallery = buildBrandingGalleryPayload(options.brandingGallery);
  }
  return applyClubFontSizeKeyAliases(body);
}

/** Always attach gallery to a club PUT body (required for backend persistence). */
export function withBrandingGallery(body, gallery) {
  if (!body || typeof body !== "object") return { brandinggallery: [] };
  return {
    ...body,
    brandinggallery: buildBrandingGalleryPayload(
      Array.isArray(gallery) ? gallery : []
    ),
  };
}

/**
 * Full club PUT: omit empty optional URL fields so Joi/Express URL rules do not 422 on "".
 * @param {Record<string, unknown>} body
 */
export function sanitizeClubUpdateRequestBody(body) {
  if (!body || typeof body !== "object") return body;
  const out = { ...body };
  const optionalUrlKeys = ["logo", ...CLUB_BRAND_BACKGROUND_IMAGE_KEYS];
  optionalUrlKeys.forEach((k) => {
    if (out[k] === "") delete out[k];
  });
  return applyClubFontSizeKeyAliases(out);
}
